import Vue from 'vue'
import Vuex from 'vuex'
import Meta from 'vue-meta'
import ClientOnly from 'vue-client-only'
import NoSsr from 'vue-no-ssr'
import { createRouter } from './router.js'
import NuxtChild from './components/nuxt-child.js'
import NuxtError from './components/nuxt-error.vue'
import Nuxt from './components/nuxt.js'
import App from './App.js'
import { setContext, getLocation, getRouteData, normalizeError } from './utils'
import { createStore } from './store.js'

/* Plugins */

import nuxt_plugin_plugin_2cd249f0 from 'nuxt_plugin_plugin_2cd249f0' // Source: ./components/plugin.js (mode: 'all')
import nuxt_plugin_sentryserver_39afd83c from 'nuxt_plugin_sentryserver_39afd83c' // Source: ./sentry.server.js (mode: 'server')
import nuxt_plugin_sentryclient_7d6ea25a from 'nuxt_plugin_sentryclient_7d6ea25a' // Source: ./sentry.client.js (mode: 'client')
import nuxt_plugin_cookieuniversalnuxt_2420f5c6 from 'nuxt_plugin_cookieuniversalnuxt_2420f5c6' // Source: ./cookie-universal-nuxt.js (mode: 'all')
import nuxt_plugin_clipboard_5605f67e from 'nuxt_plugin_clipboard_5605f67e' // Source: ./clipboard.js (mode: 'client')
import nuxt_plugin_pluginutils_6989dbdd from 'nuxt_plugin_pluginutils_6989dbdd' // Source: ./nuxt-i18n/plugin.utils.js (mode: 'all')
import nuxt_plugin_pluginrouting_36fa3430 from 'nuxt_plugin_pluginrouting_36fa3430' // Source: ./nuxt-i18n/plugin.routing.js (mode: 'all')
import nuxt_plugin_pluginmain_7dda0e87 from 'nuxt_plugin_pluginmain_7dda0e87' // Source: ./nuxt-i18n/plugin.main.js (mode: 'all')
import nuxt_plugin_axios_7823521e from 'nuxt_plugin_axios_7823521e' // Source: ./axios.js (mode: 'all')
import nuxt_plugin_gfinityesportstools_089cfba5 from 'nuxt_plugin_gfinityesportstools_089cfba5' // Source: ./gfinity-esports-tools.js (mode: 'all')
import nuxt_plugin_ageApi_8518b674 from 'nuxt_plugin_ageApi_8518b674' // Source: ../plugins/ageApi.js (mode: 'all')
import nuxt_plugin_bracketApi_2937fea2 from 'nuxt_plugin_bracketApi_2937fea2' // Source: ../plugins/bracketApi.js (mode: 'all')
import nuxt_plugin_swiss_240b3cb0 from 'nuxt_plugin_swiss_240b3cb0' // Source: ../plugins/swiss.js (mode: 'all')
import nuxt_plugin_helpers_1c816d70 from 'nuxt_plugin_helpers_1c816d70' // Source: ../plugins/helpers.js (mode: 'all')
import nuxt_plugin_logApi_758b716a from 'nuxt_plugin_logApi_758b716a' // Source: ../plugins/logApi.js (mode: 'all')
import nuxt_plugin_athlosApi_5b3446e4 from 'nuxt_plugin_athlosApi_5b3446e4' // Source: ../plugins/athlosApi.js (mode: 'all')
import nuxt_plugin_scApi_f0e5d4fa from 'nuxt_plugin_scApi_f0e5d4fa' // Source: ../plugins/scApi.js (mode: 'all')
import nuxt_plugin_vtooltip_3d258741 from 'nuxt_plugin_vtooltip_3d258741' // Source: ../plugins/v-tooltip.js (mode: 'all')
import nuxt_plugin_vueloaders_49d95258 from 'nuxt_plugin_vueloaders_49d95258' // Source: ../plugins/vue-loaders.js (mode: 'all')
import nuxt_plugin_vuevuelidate_413c4103 from 'nuxt_plugin_vuevuelidate_413c4103' // Source: ../plugins/vue-vuelidate.js (mode: 'all')
import nuxt_plugin_warApi_8881b822 from 'nuxt_plugin_warApi_8881b822' // Source: ../plugins/warApi.js (mode: 'all')
import nuxt_plugin_recylescroller_5e16e570 from 'nuxt_plugin_recylescroller_5e16e570' // Source: ../plugins/recyle-scroller.js (mode: 'all')
import nuxt_plugin_vueinfiniteloading_5f280597 from 'nuxt_plugin_vueinfiniteloading_5f280597' // Source: ../plugins/vue-infinite-loading.js (mode: 'client')

// Component: <ClientOnly>
Vue.component(ClientOnly.name, ClientOnly)

// TODO: Remove in Nuxt 3: <NoSsr>
Vue.component(NoSsr.name, {
  ...NoSsr,
  render (h, ctx) {
    if (process.client && !NoSsr._warned) {
      NoSsr._warned = true

      console.warn('<no-ssr> has been deprecated and will be removed in Nuxt 3, please use <client-only> instead')
    }
    return NoSsr.render(h, ctx)
  }
})

// Component: <NuxtChild>
Vue.component(NuxtChild.name, NuxtChild)
Vue.component('NChild', NuxtChild)

// Component NuxtLink is imported in server.js or client.js

// Component: <Nuxt>
Vue.component(Nuxt.name, Nuxt)

Object.defineProperty(Vue.prototype, '$nuxt', {
  get() {
    const globalNuxt = this.$root ? this.$root.$options.$nuxt : null
    if (process.client && !globalNuxt && typeof window !== 'undefined') {
      return window.$nuxt
    }
    return globalNuxt
  },
  configurable: true
})

Vue.use(Meta, {"keyName":"head","attribute":"data-n-head","ssrAttribute":"data-n-head-ssr","tagIDKeyName":"hid"})

const defaultTransition = {"name":"page","mode":"out-in","appear":true,"appearClass":"appear","appearActiveClass":"appear-active","appearToClass":"appear-to"}

const originalRegisterModule = Vuex.Store.prototype.registerModule

function registerModule (path, rawModule, options = {}) {
  const preserveState = process.client && (
    Array.isArray(path)
      ? !!path.reduce((namespacedState, path) => namespacedState && namespacedState[path], this.state)
      : path in this.state
  )
  return originalRegisterModule.call(this, path, rawModule, { preserveState, ...options })
}

async function createApp(ssrContext, config = {}) {
  const store = createStore(ssrContext)
  const router = await createRouter(ssrContext, config, { store })

  // Add this.$router into store actions/mutations
  store.$router = router

  // Create Root instance

  // here we inject the router and store to all child components,
  // making them available everywhere as `this.$router` and `this.$store`.
  const app = {
    head: {"title":"Clash of Clans - Esports","htmlAttrs":{"lang":"en","class":"in-app"},"meta":[{"charset":"utf-8"},{"name":"viewport","content":"width=device-width, initial-scale=1, maximum-scale=1, user-scalable=0"},{"hid":"description","name":"description","content":"Play Clash of Clans esports."}],"link":[{"rel":"icon","type":"image\u002Fx-icon","href":"\u002Ffavicon.ico"}],"bodyAttrs":{"class":"antialiased"},"style":[],"script":[]},

    store,
    router,
    nuxt: {
      defaultTransition,
      transitions: [defaultTransition],
      setTransitions (transitions) {
        if (!Array.isArray(transitions)) {
          transitions = [transitions]
        }
        transitions = transitions.map((transition) => {
          if (!transition) {
            transition = defaultTransition
          } else if (typeof transition === 'string') {
            transition = Object.assign({}, defaultTransition, { name: transition })
          } else {
            transition = Object.assign({}, defaultTransition, transition)
          }
          return transition
        })
        this.$options.nuxt.transitions = transitions
        return transitions
      },

      err: null,
      errPageReady: false,
      dateErr: null,
      error (err) {
        err = err || null
        app.context._errored = Boolean(err)
        err = err ? normalizeError(err) : null
        let nuxt = app.nuxt // to work with @vue/composition-api, see https://github.com/nuxt/nuxt.js/issues/6517#issuecomment-573280207
        if (this) {
          nuxt = this.nuxt || this.$options.nuxt
        }
        nuxt.dateErr = Date.now()
        nuxt.err = err
        nuxt.errPageReady = false
        // Used in src/server.js
        if (ssrContext) {
          ssrContext.nuxt.error = err
        }
        return err
      }
    },
    ...App
  }

  // Make app available into store via this.app
  store.app = app

  const next = ssrContext ? ssrContext.next : location => app.router.push(location)
  // Resolve route
  let route
  if (ssrContext) {
    route = router.resolve(ssrContext.url).route
  } else {
    const path = getLocation(router.options.base, router.options.mode)
    route = router.resolve(path).route
  }

  // Set context to app.context
  await setContext(app, {
    store,
    route,
    next,
    error: app.nuxt.error.bind(app),
    payload: ssrContext ? ssrContext.payload : undefined,
    req: ssrContext ? ssrContext.req : undefined,
    res: ssrContext ? ssrContext.res : undefined,
    beforeRenderFns: ssrContext ? ssrContext.beforeRenderFns : undefined,
    beforeSerializeFns: ssrContext ? ssrContext.beforeSerializeFns : undefined,
    ssrContext
  })

  function inject(key, value) {
    if (!key) {
      throw new Error('inject(key, value) has no key provided')
    }
    if (value === undefined) {
      throw new Error(`inject('${key}', value) has no value provided`)
    }

    key = '$' + key
    // Add into app
    app[key] = value
    // Add into context
    if (!app.context[key]) {
      app.context[key] = value
    }

    // Add into store
    store[key] = app[key]

    // Check if plugin not already installed
    const installKey = '__nuxt_' + key + '_installed__'
    if (Vue[installKey]) {
      return
    }
    Vue[installKey] = true
    // Call Vue.use() to install the plugin into vm
    Vue.use(() => {
      if (!Object.prototype.hasOwnProperty.call(Vue.prototype, key)) {
        Object.defineProperty(Vue.prototype, key, {
          get () {
            return this.$root.$options[key]
          }
        })
      }
    })
  }

  // Inject runtime config as $config
  inject('config', config)

  if (process.client) {
    // Replace store state before plugins execution
    if (window.__NUXT__ && window.__NUXT__.state) {
      store.replaceState(window.__NUXT__.state)
    }
  }

  // Add enablePreview(previewData = {}) in context for plugins
  if (process.static && process.client) {
    app.context.enablePreview = function (previewData = {}) {
      app.previewData = Object.assign({}, previewData)
      inject('preview', previewData)
    }
  }
  // Plugin execution

  if (typeof nuxt_plugin_plugin_2cd249f0 === 'function') {
    await nuxt_plugin_plugin_2cd249f0(app.context, inject)
  }

  if (process.server && typeof nuxt_plugin_sentryserver_39afd83c === 'function') {
    await nuxt_plugin_sentryserver_39afd83c(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_sentryclient_7d6ea25a === 'function') {
    await nuxt_plugin_sentryclient_7d6ea25a(app.context, inject)
  }

  if (typeof nuxt_plugin_cookieuniversalnuxt_2420f5c6 === 'function') {
    await nuxt_plugin_cookieuniversalnuxt_2420f5c6(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_clipboard_5605f67e === 'function') {
    await nuxt_plugin_clipboard_5605f67e(app.context, inject)
  }

  if (typeof nuxt_plugin_pluginutils_6989dbdd === 'function') {
    await nuxt_plugin_pluginutils_6989dbdd(app.context, inject)
  }

  if (typeof nuxt_plugin_pluginrouting_36fa3430 === 'function') {
    await nuxt_plugin_pluginrouting_36fa3430(app.context, inject)
  }

  if (typeof nuxt_plugin_pluginmain_7dda0e87 === 'function') {
    await nuxt_plugin_pluginmain_7dda0e87(app.context, inject)
  }

  if (typeof nuxt_plugin_axios_7823521e === 'function') {
    await nuxt_plugin_axios_7823521e(app.context, inject)
  }

  if (typeof nuxt_plugin_gfinityesportstools_089cfba5 === 'function') {
    await nuxt_plugin_gfinityesportstools_089cfba5(app.context, inject)
  }

  if (typeof nuxt_plugin_ageApi_8518b674 === 'function') {
    await nuxt_plugin_ageApi_8518b674(app.context, inject)
  }

  if (typeof nuxt_plugin_bracketApi_2937fea2 === 'function') {
    await nuxt_plugin_bracketApi_2937fea2(app.context, inject)
  }

  if (typeof nuxt_plugin_swiss_240b3cb0 === 'function') {
    await nuxt_plugin_swiss_240b3cb0(app.context, inject)
  }

  if (typeof nuxt_plugin_helpers_1c816d70 === 'function') {
    await nuxt_plugin_helpers_1c816d70(app.context, inject)
  }

  if (typeof nuxt_plugin_logApi_758b716a === 'function') {
    await nuxt_plugin_logApi_758b716a(app.context, inject)
  }

  if (typeof nuxt_plugin_athlosApi_5b3446e4 === 'function') {
    await nuxt_plugin_athlosApi_5b3446e4(app.context, inject)
  }

  if (typeof nuxt_plugin_scApi_f0e5d4fa === 'function') {
    await nuxt_plugin_scApi_f0e5d4fa(app.context, inject)
  }

  if (typeof nuxt_plugin_vtooltip_3d258741 === 'function') {
    await nuxt_plugin_vtooltip_3d258741(app.context, inject)
  }

  if (typeof nuxt_plugin_vueloaders_49d95258 === 'function') {
    await nuxt_plugin_vueloaders_49d95258(app.context, inject)
  }

  if (typeof nuxt_plugin_vuevuelidate_413c4103 === 'function') {
    await nuxt_plugin_vuevuelidate_413c4103(app.context, inject)
  }

  if (typeof nuxt_plugin_warApi_8881b822 === 'function') {
    await nuxt_plugin_warApi_8881b822(app.context, inject)
  }

  if (typeof nuxt_plugin_recylescroller_5e16e570 === 'function') {
    await nuxt_plugin_recylescroller_5e16e570(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_vueinfiniteloading_5f280597 === 'function') {
    await nuxt_plugin_vueinfiniteloading_5f280597(app.context, inject)
  }

  // Lock enablePreview in context
  if (process.static && process.client) {
    app.context.enablePreview = function () {
      console.warn('You cannot call enablePreview() outside a plugin.')
    }
  }

  // Wait for async component to be resolved first
  await new Promise((resolve, reject) => {
    // Ignore 404s rather than blindly replacing URL in browser
    if (process.client) {
      const { route } = router.resolve(app.context.route.fullPath)
      if (!route.matched.length) {
        return resolve()
      }
    }
    router.replace(app.context.route.fullPath, resolve, (err) => {
      // https://github.com/vuejs/vue-router/blob/v3.4.3/src/util/errors.js
      if (!err._isRouter) return reject(err)
      if (err.type !== 2 /* NavigationFailureType.redirected */) return resolve()

      // navigated to a different route in router guard
      const unregister = router.afterEach(async (to, from) => {
        if (process.server && ssrContext && ssrContext.url) {
          ssrContext.url = to.fullPath
        }
        app.context.route = await getRouteData(to)
        app.context.params = to.params || {}
        app.context.query = to.query || {}
        unregister()
        resolve()
      })
    })
  })

  return {
    store,
    app,
    router
  }
}

export { createApp, NuxtError }
